import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";
import "./screen.css";
import {
  DeleteOutlineOutlined,
  DeleteOutlined,
  DesktopWindowsOutlined,
  DesktopMacOutlined,
  ModeEditOutlineOutlined,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import {
  deleteScreen,
  addScreen,
  getScreens,
  screenFlagOff,
  updateScreen,
} from "../../Redux/Actions/screenAction";
import { useNavigate } from "react-router-dom";
import TvIcon from "@mui/icons-material/Tv";
import { styled, alpha } from "@mui/material/styles";
import { ReactComponent as AddScreenIcon } from "../../Assets/images/addScreenIcon.svg";
import PrimarySearchBar from "../../components/searchBar/searchbar";
import DeleteModel from "../../components/deletemodel/DeleteModel";
import Loader from "../../components/loader/Loader";
import toast from "react-hot-toast";
import { canEditCms, isSuperAdmin } from "../../utils/accessControl";
import { useContextUserId } from "../../hooks/useSupportContext";
import { tr } from "../../i18n";

const CustomTableCell = styled(TableCell)(() => ({
  color: "#374151",
  fontSize: "10px",
  fontWeight: "600",
  fontFamily: "'Electrolize', sans-serif",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  padding: "8px 10px",
  borderBottom: "2px solid #0d9488",
  backgroundColor: "#fafbfc",
}));

const BodyCell = styled(TableCell)(() => ({
  color: "#111827",
  fontSize: "12px",
  fontWeight: "400",
  fontFamily: "'Electrolize', sans-serif",
  lineHeight: 1.35,
  padding: "9px 10px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
}));

function Screen({ socketRef, user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canEdit = canEditCms(user);
  const contextUserId = useContextUserId();
  const {
    screens,
    isScreenAdded,
    isAdded,
    isScreenDeleted,
    mac,
    screenLoading,
    isLoaded,
    isScreenUpdated,
    pagination,
  } = useSelector((state) => state.Screen);
  const [openModel, setOpenModel] = useState(false);
  const [nMac, setNMac] = useState("");
  const [screenId, setScreenId] = useState({ id: "", mac: "" });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [orientation, setOrietation] = useState("");
  const [location, setLocation] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const macRef = useRef();
  const [textVal, setTextVal] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [syncList, setSyncList] = useState([]);
  const [errors, setErrors] = useState({});
  const [statusFilter, setStatusFilter] = useState("all"); // all | online | offline
  const [locationFilter, setLocationFilter] = useState("all");

  useEffect(() => {
    if (!contextUserId) return;
    socketRef.current.emit("leaving--connection");
    loadScreens(1, itemsPerPage, searchTerm);
  }, [contextUserId]);

  const loadScreens = (page = currentPage, limit = itemsPerPage, search = searchTerm) => {
    dispatch(getScreens(contextUserId, { page, limit, search }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadScreens(page, itemsPerPage, searchTerm);
  };
  const handleItemsPerPageChange = (e) => {
    const limit = parseInt(e.target.value, 10);
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadScreens(1, limit, searchTerm);
  };
  // Always derive local copy from latest Redux screens for correct pagination
  const _screens = useMemo(
    () => [...screens],
    [screens, isLoaded, isAdded, isScreenDeleted, isScreenUpdated]
  );

  const locationKey = (s) => (s?.location || "").trim().toLowerCase();
  const locationOptions = useMemo(() => {
    const byKey = new Map();
    _screens.forEach((s) => {
      const key = locationKey(s);
      if (key && !byKey.has(key)) byKey.set(key, s.location.trim());
    });
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1], "sv"));
  }, [_screens]);

  const filteredScreens = useMemo(() => {
    let list = _screens;
    if (statusFilter === "online") list = list.filter((s) => s.onlinesStatus === true);
    if (statusFilter === "offline") list = list.filter((s) => s.onlinesStatus === false);
    if (locationFilter !== "all") list = list.filter((s) => locationKey(s) === locationFilter);
    return list;
  }, [_screens, statusFilter, locationFilter]);
  useEffect(() => {
    if (isAdded) {
      // Emit with the added screen's mac from API response so the device receives updated playlist/schedule
      if (mac && socketRef.current) {
        const macToEmit = mac;
        setTimeout(() => {
          if (socketRef.current) {
            console.log('[Screen] Emitting event:delegate for mac:', macToEmit);
            socketRef.current.emit("event:delegate", { detail: { mac: macToEmit } });
          }
        }, 500);
      } else if (!mac) {
        console.warn('[Screen] Add screen: no mac in response, cannot emit to device');
      }
      dispatch(screenFlagOff());
      setCurrentPage(1);
      loadScreens(1, itemsPerPage, searchTerm);
    }
    if (isScreenDeleted) {
      dispatch(screenFlagOff());
      loadScreens(currentPage, itemsPerPage, searchTerm);
    }
    if (isScreenUpdated) {
      socketRef.current?.emit("event:delegate", { detail: { mac } });
      dispatch(screenFlagOff());
      toast.success(tr("Screen updated."));
      loadScreens(currentPage, itemsPerPage, searchTerm);
    }
  }, [isAdded, isScreenDeleted, isScreenUpdated, mac]);
  const emitOnDeleteScreen = (macToEmit) => {
    const id = setTimeout(() => {
      socketRef.current?.emit("event:delegate", { detail: { mac: macToEmit } });
      clearTimeout(id);
    }, 2000);
  };
  const deleteScreenHandler = (id, macToEmit) => {
    setScreenId({ id: id, mac: macToEmit });
    setOpenDeleteDialog(true);
  };
  const editScreenHandler = (id, macToEmit) => {
    const _selectedScreen = _screens.find((screen) => screen._id == id);
    setName(_selectedScreen?.name);
    setCode(_selectedScreen?.code);
    setOrietation(_selectedScreen?.orientation);
    setLocation(_selectedScreen?.location);
    setScreenId({ id: id, mac: macToEmit });
    setErrors({});
    setOpenEditDialog(true);
  };
  const confirmDeleteScreen = () => {
    dispatch(deleteScreen(screenId.id));
    emitOnDeleteScreen(screenId.mac);
    setOpenDeleteDialog(false);
  };
  const validateAddForm = () => {
    const newErrors = {};
    if (!code.trim()) newErrors.code = "Screen code is required";
    if (!name.trim()) newErrors.name = "Screen name is required";
    if (!orientation) newErrors.orientation = "Orientation is required";
    if (!location.trim()) newErrors.location = "Location is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Screen name is required";
    if (!orientation) newErrors.orientation = "Orientation is required";
    if (!location.trim()) newErrors.location = "Location is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = () => {
    if (!validateAddForm()) return;
    dispatch(
      addScreen(
        {
          name,
          orientation,
          location,
          user: contextUserId,
        },
        code
      )
    );
    setOpenModel(false);
    setCode("");
    setName("");
    setOrietation("");
    setLocation("");
    setErrors({});
  };

  const onUpdate = () => {
    if (!validateEditForm()) return;
    dispatch(
      updateScreen(
        {
          name,
          orientation,
          location
        },
        screenId.id
      )
    );
    setOpenEditDialog(false);
    setCode("");
    setName("");
    setOrietation("");
    setLocation("");
    setErrors({});
  };

  const searchHandler = (e) => {
    setTextVal(e);
    setSearchTerm(e);
    setCurrentPage(1);
    loadScreens(1, itemsPerPage, e);
  };

  const onGetSyncList = (e) => {
    const { value, checked } = e.target;
    checked
      ? setSyncList((prev) => [...prev, value])
      : setSyncList(syncList.filter((_sync) => _sync !== value));
  };
  const onSyncScreen = () => {
    if (syncList.length > 0) {
      console.log(syncList);
      syncList.forEach(async (_sync, index) => {
        await socketRef.current.emit(
          "screen:sync",
          { detail: { mac: _sync } },
          function (done, _mac) {
            // if (done) {
            setSyncList((prevSyncList) =>
              prevSyncList.filter((mac) => mac !== _sync)
            );
            //}
          }
        );
      });
    }
  };

  function timeSince(date) {
    var seconds = Math.floor((new Date() - date) / 1000);

    var interval = seconds / 31536000;

    if (interval > 1) {
      return Math.floor(interval) + "y";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      return Math.floor(interval) + "mon";
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return Math.floor(interval) + "d";
    }
    interval = seconds / 3600;
    if (interval > 1) {
      return Math.floor(interval) + "h";
    }
    interval = seconds / 60;
    if (interval > 1) {
      return Math.floor(interval) + "min";
    }
    return Math.floor(seconds) + "sec";
  }


  return (
    <>
      <Layout title={tr("Screen")} user={user}>
        <input ref={macRef} type="hidden" value={mac ?? ''} readOnly aria-hidden="true" />
        <div className="parent-container screen-page">
          <Paper
            elevation={0}
            className="screen-toolbar"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "14px",
              padding: "12px 20px",
              borderRadius: "14px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
              mb: "4px",
            }}
          >
            <div className="toolbar-search">
              <PrimarySearchBar value={textVal} onChange={searchHandler} />
            </div>
            <div className="content-filter-toolbar content-filter-toolbar--inside screen-filter-toolbar screen-filter-toolbar--inline">
              <div className="pl-toolbar-filters">
                <div className="pl-filter-group">
                  <label htmlFor="screen-status-filter" className="ct-filter-select-label">{tr("Status")}</label>
                  <select
                    id="screen-status-filter"
                    className="pl-filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">{tr("All")}</option>
                    <option value="online">{tr("Online")}</option>
                    <option value="offline">{tr("Offline")}</option>
                  </select>
                </div>
                {locationOptions.length > 1 && (
                  <div className="pl-filter-group">
                    <label htmlFor="screen-location-filter" className="ct-filter-select-label">{tr("Location")}</label>
                    <select
                      id="screen-location-filter"
                      className="pl-filter-select"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                    >
                      <option value="all">{tr("All")}</option>
                      {locationOptions.map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="toolbar-actions">
              {canEdit && (
                <button
                  className="toolbar-btn toolbar-btn--outline"
                  onClick={onSyncScreen}
                >
                  {tr("Sync")}{syncList.length > 0 && ` (${syncList.length})`}
                </button>
              )}
              {canEdit && (
                <button className="toolbar-btn toolbar-btn--outline">
                  {tr("Update All")}
                </button>
              )}
              {canEdit && (
                <a
                  className="toolbar-btn toolbar-btn--outline"
                  href="/web-player.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                >
                  {tr("Web player")}
                </a>
              )}
              {canEdit && (
                <button
                  className="toolbar-btn toolbar-btn--primary"
                  onClick={() => { setCode(""); setName(""); setOrietation(""); setLocation(""); setErrors({}); setOpenModel(true); }}
                >
                  {tr("+ Add Screen")}
                </button>
              )}
            </div>
          </Paper>
          <div className="fl-table-card screen-table">
            <TableContainer component="div" sx={{ maxHeight: "calc(100vh - 380px)", overflow: "auto" }}>
                <Table stickyHeader size="small" className="screen-data-table" sx={{ minWidth: { xs: 480, md: 900 } }}>
                  <TableHead>
                    <TableRow className="screen-table-header-row">
                      <CustomTableCell align="center" sx={{ width: 50 }}></CustomTableCell>
                      <CustomTableCell align="center">{tr("ID")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Code")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Name")}</CustomTableCell>
                      {isSuperAdmin(user) && (
                        <CustomTableCell align="center">{tr("Mac")}</CustomTableCell>
                      )}
                      {isSuperAdmin(user) && (
                        <CustomTableCell align="center">{tr("User")}</CustomTableCell>
                      )}
                      <CustomTableCell align="center">{tr("Location")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Software")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Playlist")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Last Update")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Status")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Last Active")}</CustomTableCell>
                      <CustomTableCell align="center">{tr("Actions")}</CustomTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {screenLoading && _screens.length === 0 ? (
                      <TableRow>
                        <BodyCell colSpan={13} align="center" sx={{ py: 4 }}>
                          <Loader />
                        </BodyCell>
                      </TableRow>
                    ) : filteredScreens.length > 0 ? (
                      filteredScreens.map((screen, index) => (
                        <TableRow key={screen._id || index} className="screen-table-body-row">
                          <BodyCell align="center">
                            <input
                              type="checkbox"
                              style={{
                                height: "14px",
                                width: "14px",
                                accentColor: "#008080",
                                cursor: "pointer",
                              }}
                              value={screen.mac}
                              onChange={onGetSyncList}
                            />
                          </BodyCell>
                          <BodyCell align="center">
                            {(() => {
                              const idx = _screens.findIndex((s) => s._id === screen._id);
                              return idx >= 0 ? (currentPage - 1) * itemsPerPage + idx + 1 : index + 1;
                            })()}
                          </BodyCell>
                          <BodyCell align="center">
                            <span className="screen-code-badge">
                              {screen.code}
                            </span>
                          </BodyCell>
                          <BodyCell align="center">
                            {screen.name || "—"}
                          </BodyCell>
                          {isSuperAdmin(user) && (
                            <BodyCell align="center">
                              {screen.mac}
                            </BodyCell>
                          )}
                          {isSuperAdmin(user) && (
                            <BodyCell align="center">{screen?.user?.username || "—"}</BodyCell>
                          )}
                          <BodyCell align="center">{screen?.location ?? "N/A"}</BodyCell>
                          <BodyCell align="center">{screen?.device_details?.software || "—"}</BodyCell>
                          <BodyCell align="center">
                            {screen?.playlist?._id ? (
                              <button
                                type="button"
                                className="screen-playlist-link"
                                onClick={() => navigate(`/playlist/${screen.playlist._id}`)}
                              >
                                {screen.playlist.name || tr("Playlist")}
                              </button>
                            ) : screen?.playlist?.name || (
                              <span className="screen-no-playlist">{tr("No Playlist")}</span>
                            )}
                          </BodyCell>
                          <BodyCell align="center">
                            {screen?.lastUpdated !== undefined
                              ? `${timeSince(new Date(screen?.lastUpdated).getTime())} ago`
                              : "N/A"}
                          </BodyCell>
                          <BodyCell align="center">
                            <div
                              className={`status-badge ${screen.onlinesStatus ? "success" : "danger"}`}
                            />
                          </BodyCell>
                          <BodyCell align="center">
                            {`${new Date(screen?.lastActive).toDateString()} ${new Date(
                              screen?.lastActive
                            ).toLocaleTimeString()}`}
                          </BodyCell>
                          <BodyCell align="center">
                            <div className="screen-actions-icons">
                              {canEdit && (
                                <>
                                  <ModeEditOutlineOutlined
                                    className="screen-icon screen-icon--edit"
                                    onClick={() => editScreenHandler(screen._id, screen.mac)}
                                  />
                                  <DeleteOutlineOutlined
                                    className="screen-icon screen-icon--delete"
                                    onClick={() => deleteScreenHandler(screen._id, screen.mac)}
                                  />
                                  <DesktopWindowsOutlined
                                    className="screen-icon screen-icon--settings"
                                    onClick={() => navigate(`/setting/${screen._id}`)}
                                  />
                                </>
                              )}
                            </div>
                          </BodyCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <BodyCell colSpan={13} align="center" className="screen-empty-cell">
                          {statusFilter === "online"
                            ? tr("No online screens.")
                            : statusFilter === "offline"
                            ? tr("No offline screens.")
                            : tr("No screens found.")}
                        </BodyCell>
                      </TableRow>
                    )}
                    {screenLoading && _screens.length > 0 && (
                      <TableRow>
                        <BodyCell colSpan={13} align="center" sx={{ py: 2 }}>
                          <Loader />
                        </BodyCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
            </TableContainer>
            {pagination && (
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 20, 50]}
                count={pagination.totalItems || 0}
                rowsPerPage={itemsPerPage}
                page={currentPage - 1}
                onPageChange={(e, newPage) => handlePageChange(newPage + 1)}
                onRowsPerPageChange={handleItemsPerPageChange}
                sx={{
                  borderTop: "1px solid #E5E7EB",
                  fontFamily: "'Electrolize', sans-serif",
                  color: "#374151",
                  flexShrink: 0,
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select": {
                    fontSize: "13px",
                    fontFamily: "'Electrolize', sans-serif",
                    color: "#374151",
                  },
                }}
              />
            )}
          </div>
        </div>
      </Layout>

      <DeleteModel
        open={openDeleteDialog}
        onClose={(e) => setOpenDeleteDialog(e)}
        onConfirm={confirmDeleteScreen}
      />

      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openModel}
        onClose={() => { setOpenModel(false); setErrors({}); }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            borderBottom: "2px solid #008080",
            fontSize: "18px",
            fontWeight: 700,
            fontFamily: "'Electrolize', sans-serif",
            py: 2,
          }}
        >
          {tr("Add Screen")}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 3, pb: 2 }}>
          <div className="screen-form">
            <div className="icon-screen">
              <DesktopMacOutlined
                sx={{ fontSize: "80px", color: "#008080" }}
              />
            </div>
            <div className="form-fields">
              <TextField
                value={code}
                onChange={(e) => { setCode(e.target.value); if (errors.code) setErrors((prev) => ({ ...prev, code: "" })); }}
                type="text"
                variant="outlined"
                label={tr("Screen Code")}
                placeholder={tr("Enter screen code")}
                fullWidth
                required
                size="small"
                error={!!errors.code}
                helperText={errors.code || ""}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "12px",
                    marginLeft: 0,
                  },
                }}
              />
            </div>
            <div className="form-fields">
              <TextField
                value={name}
                onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: "" })); }}
                type="text"
                variant="outlined"
                label={tr("Screen Name")}
                placeholder={tr("Enter screen name")}
                fullWidth
                required
                size="small"
                error={!!errors.name}
                helperText={errors.name || ""}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "12px",
                    marginLeft: 0,
                  },
                }}
              />
            </div>
            <div className="form-fields">
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                required
                error={!!errors.orientation}
                sx={{ mb: 2.5 }}
              >
                <InputLabel
                  id="add-orientation-label"
                  sx={{ fontFamily: "'Electrolize', sans-serif", fontSize: "14px" }}
                >
                  {tr("Orientation")}
                </InputLabel>
                <Select
                  labelId="add-orientation-label"
                  value={orientation}
                  label={tr("Orientation")}
                  onChange={(e) => { setOrietation(e.target.value); if (errors.orientation) setErrors((prev) => ({ ...prev, orientation: "" })); }}
                  sx={{
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  }}
                >
                  <MenuItem value="0">0°</MenuItem>
                  <MenuItem value="90">90°</MenuItem>
                  <MenuItem value="180">180°</MenuItem>
                  <MenuItem value="270">270°</MenuItem>
                </Select>
                {errors.orientation && (
                  <span style={{ color: "#d32f2f", fontSize: "12px", fontFamily: "'Electrolize', sans-serif", marginTop: "4px" }}>
                    {errors.orientation}
                  </span>
                )}
              </FormControl>
            </div>
            <div className="form-fields">
              <TextField
                value={location}
                onChange={(e) => { setLocation(e.target.value); if (errors.location) setErrors((prev) => ({ ...prev, location: "" })); }}
                type="text"
                variant="outlined"
                label={tr("Location")}
                placeholder={tr("Enter location")}
                fullWidth
                required
                size="small"
                error={!!errors.location}
                helperText={errors.location || ""}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "12px",
                    marginLeft: 0,
                  },
                }}
              />
            </div>

            <Box
              sx={{
                display: "flex",
                mt: 4,
                mb: 2,
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button
                className="btn-cancel-screen"
                onClick={() => { setOpenModel(false); setErrors({}); }}
              >
                {tr("Cancel")}
              </button>
              <button onClick={submit} className="btn primary btn-save-screen">
                {tr("Save")}
              </button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openEditDialog}
        onClose={() => { setOpenEditDialog(false); setErrors({}); }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            borderBottom: "2px solid #008080",
            fontSize: "18px",
            fontWeight: 700,
            fontFamily: "'Electrolize', sans-serif",
            py: 2,
          }}
        >
          {tr("Edit Screen")}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 3, pb: 2 }}>
          <div className="screen-form">
            <div className="icon-screen">
              <DesktopMacOutlined
                sx={{ fontSize: "80px", color: "#008080" }}
              />
            </div>
            <div className="form-fields">
              <TextField
                value={code}
                disabled
                type="text"
                variant="outlined"
                label={tr("Screen Code")}
                fullWidth
                size="small"
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                    backgroundColor: "#F9FAFB",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                }}
              />
            </div>
            <div className="form-fields">
              <TextField
                value={name}
                onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: "" })); }}
                type="text"
                variant="outlined"
                label={tr("Screen Name")}
                placeholder={tr("Enter screen name")}
                fullWidth
                required
                size="small"
                error={!!errors.name}
                helperText={errors.name || ""}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "12px",
                    marginLeft: 0,
                  },
                }}
              />
            </div>
            <div className="form-fields">
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                required
                error={!!errors.orientation}
                sx={{ mb: 2.5 }}
              >
                <InputLabel
                  id="edit-orientation-label"
                  sx={{ fontFamily: "'Electrolize', sans-serif", fontSize: "14px" }}
                >
                  {tr("Orientation")}
                </InputLabel>
                <Select
                  labelId="edit-orientation-label"
                  value={orientation}
                  label={tr("Orientation")}
                  onChange={(e) => { setOrietation(e.target.value); if (errors.orientation) setErrors((prev) => ({ ...prev, orientation: "" })); }}
                  sx={{
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  }}
                >
                  <MenuItem value="0">0°</MenuItem>
                  <MenuItem value="90">90°</MenuItem>
                  <MenuItem value="180">180°</MenuItem>
                  <MenuItem value="270">270°</MenuItem>
                </Select>
                {errors.orientation && (
                  <span style={{ color: "#d32f2f", fontSize: "12px", fontFamily: "'Electrolize', sans-serif", marginTop: "4px" }}>
                    {errors.orientation}
                  </span>
                )}
              </FormControl>
            </div>
            <div className="form-fields">
              <TextField
                value={location}
                onChange={(e) => { setLocation(e.target.value); if (errors.location) setErrors((prev) => ({ ...prev, location: "" })); }}
                type="text"
                variant="outlined"
                label={tr("Location")}
                placeholder={tr("Enter location")}
                fullWidth
                required
                size="small"
                error={!!errors.location}
                helperText={errors.location || ""}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "'Electrolize', sans-serif",
                    fontSize: "12px",
                    marginLeft: 0,
                  },
                }}
              />
            </div>

            <Box
              sx={{
                display: "flex",
                mt: 4,
                mb: 2,
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button
                className="btn-cancel-screen"
                onClick={() => { setOpenEditDialog(false); setErrors({}); }}
              >
                {tr("Cancel")}
              </button>
              <button
                onClick={onUpdate}
                className="btn primary btn-save-screen"
              >
                {tr("Update")}
              </button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Screen;
